/**
 * Per-user learned department classifications.
 *
 * When a user moves an item to a different supermarket department on the
 * shopping list, we remember `(canonicalKey(name) → department)` against
 * their email. The next time we build a shopping list for that user we
 * apply these overrides BEFORE the static keyword classifier, so future
 * lists put the same ingredient in the right aisle automatically.
 *
 * Per-user, never global: one person's "Halloumi → Dairy & Eggs" doesn't
 * affect anyone else.
 */

import { supabase } from "@/integrations/supabase/client";
import type { Department } from "./departments";

const cache = new Map<string, Map<string, Department>>();

const cacheKey = (email: string) => email.trim().toLowerCase();

/**
 * Read all overrides for a user. Cached per session so repeated list
 * generations don't refetch.
 */
export const getUserDepartmentOverrides = async (
  email: string,
): Promise<Map<string, Department>> => {
  const k = cacheKey(email);
  const cached = cache.get(k);
  if (cached) return cached;

  const { data, error } = await supabase
    .from("department_overrides")
    .select("ingredient_key, department")
    .eq("email", k);

  if (error) {
    console.error("[departmentOverrides] load failed", error);
    const empty = new Map<string, Department>();
    cache.set(k, empty);
    return empty;
  }

  const map = new Map<string, Department>();
  for (const row of data || []) {
    map.set(row.ingredient_key, row.department as Department);
  }
  cache.set(k, map);
  return map;
};

export const setDepartmentOverride = async (
  email: string,
  ingredientKey: string,
  department: Department,
): Promise<void> => {
  const k = cacheKey(email);
  if (!ingredientKey) return;
  const { error } = await supabase
    .from("department_overrides")
    .upsert(
      { email: k, ingredient_key: ingredientKey, department },
      { onConflict: "email,ingredient_key" },
    );
  if (error) {
    console.error("[departmentOverrides] save failed", error);
    throw error;
  }
  // Update cache.
  const map = cache.get(k) || new Map<string, Department>();
  map.set(ingredientKey, department);
  cache.set(k, map);
};

export const removeDepartmentOverride = async (
  email: string,
  ingredientKey: string,
): Promise<void> => {
  const k = cacheKey(email);
  const { error } = await supabase
    .from("department_overrides")
    .delete()
    .eq("email", k)
    .eq("ingredient_key", ingredientKey);
  if (error) {
    console.error("[departmentOverrides] delete failed", error);
    throw error;
  }
  cache.get(k)?.delete(ingredientKey);
};

export const resetDepartmentOverrides = async (email: string): Promise<void> => {
  const k = cacheKey(email);
  const { error } = await supabase
    .from("department_overrides")
    .delete()
    .eq("email", k);
  if (error) {
    console.error("[departmentOverrides] reset failed", error);
    throw error;
  }
  cache.delete(k);
};

/** Drop the in-memory cache (e.g. after sign-out). */
export const clearDepartmentOverrideCache = (email?: string) => {
  if (email) cache.delete(cacheKey(email));
  else cache.clear();
};
